import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import type { Role, User } from "@/types/auth";

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  /** Demo-mode login: fabricate a token from the persona. */
  login: (user: User) => void;
  /** Live login: store real tokens + the user from /auth/otp/verify. */
  loginWithTokens: (user: User, accessToken: string, refreshToken: string) => void;
  /** Replace tokens after a refresh. */
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: User) => void;
  setLanguage: (lang: "en" | "sw") => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        login: (user) =>
          set({ user, accessToken: `demo-token-${user.role}`, refreshToken: null, isAuthenticated: true }),
        loginWithTokens: (user, accessToken, refreshToken) =>
          set({ user, accessToken, refreshToken, isAuthenticated: true }),
        setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
        setUser: (user) => set({ user }),
        setLanguage: (language) =>
          set((s) => (s.user ? { user: { ...s.user, language } } : s)),
        logout: () => set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false }),
      }),
      { name: "stocksync-auth" },
    ),
    { name: "auth" },
  ),
);

export function useRole(): Role | null {
  return useAuthStore((s) => s.user?.role ?? null);
}
